/* eslint-disable @typescript-eslint/naming-convention */
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { Action, ActionCreator, AnyAction } from 'redux';

import type { FlagName } from '../shared/featureFlags/types';
import type { Category, IssueTypesSummary } from '../shared/issuesV2';
import type { JiraComponent, JiraCustomField } from '../shared/jira';
import type { RevisionLicense } from '../shared/licenses/common';
import type {
  QualityRule as CommonQualityRule,
  Jira,
  Notification,
  OrganizationAccessLevel,
  SamlStrategy,
  TeamReleaseGroup,
  UpdateHookScheduledInterval,
} from '../shared/model-typings';
import type { StripePriceLimit } from '../shared/model-typings/stripepricelimits';
import type { VCSHost } from '../shared/projectsV2/vcs';
import type { FossaPlanName } from '../shared/subscriptions';
import type {
  Issue as APIIssue,
  Layer as APILayer,
  LicensingRule as APILicensingRule,
  Policy as APIPolicy,
  ListMatchesResponse,
} from '../typings/api';
import type {
  CocoapodsSource,
  FossaSubscriptionPlan as CommonFossaSubscriptionPlan,
  FossaSubscriptionPlanPreview as CommonFossaSubscriptionPlanPreview,
  FetcherConfigMap,
  ILoc,
} from '../typings/common';
import type { ProjectType, RevisionCliLicenseScanType, RevisionSource } from '../typings/enums';
import type { Permission } from '../typings/permissions';

import type { APIError as APIErrorClass } from './helpers/errors';

// TODO: fix these barrels
/* eslint-disable no-barrel-files/no-barrel-files */
export type { AnnotatedLicense, License, SourcedLicense } from '../modules/licenses/types';
export type { AuditLog, SamlStrategy } from '../shared/model-typings';
export { PolicyType } from '../typings/enums';
/* eslint-enable no-barrel-files/no-barrel-files */

export type Dictionary<T> = Record<string, T>;

export type Diff<T extends string, U extends string> = ({[P in T]: P } &
{[P in U]: never } & Record<string, never>)[T];

export type SettledPromise<ValueType, RejectedReasonType> =
  | { status: 'fulfilled'; value: ValueType }
  | { status: 'rejected'; reason: RejectedReasonType };

// The FOSSA Core API responds with an APIError when it cannot process a request.
// TODO: Replace references with the actual APIError class in the errors.ts file.
export type APIError = APIErrorClass;

export type AccessLevel = 'free' | 'premium';

export type IOrganizationMember = {
  organizationId?: number;
  organization?: Organization;
};

/** @deprecated Use AsyncStatus instead. */
export type IAsyncStatus<E = APIError | string> = {
  errors?: E[];
  // error is a deprecated field
  error?: E;
  loading: boolean;
  status?: number;
};

// Note: This should not be modified, and kept as simple as possible
export type AsyncStatus = {
  /**
   * @deprecated Use the errors array instead.
   */
  error?: string | null | undefined;
  errors?: string[] | null;
  /**
   * @deprecated Use the isLoading instead.
   */
  loading: boolean;
  isLoading: boolean;
};

// This type adds an optional timestamp field to AsyncStatus type. It is used to know when the associated request was triggered
export type TimestampedAsyncStatus = AsyncStatus & {
  timestamp?: number;
};

export type PaginatedAsyncStatus = AsyncStatus & {
  hasMore: boolean;
};

// copied from common -- import once we reorg codebase
export type ITimestamp = {
  createdAt?: string;
  updatedAt?: string;
};

export type IAsync<T, E = APIError | string> = {
  data?: T;
  status: IAsyncStatus<E>;
};

export type ILocator = {
  locator?: string;
  loc?: ILoc;
};

export type Token = {
  id?: number;
  userId?: number;
  organizationId?: number;
  email?: string;
  type: 'password' | 'invite' | 'api' | 'verify_sso_domain';
  name?: string;
  token: string;
  isDisabled: boolean;
  meta?: {
    pushOnly?: boolean;
    defaultPolicyId?: number;
    defaultSecurityPolicyId?: number;
    defaultQualityPolicyId?: number;
  };
} & ITimestamp;

export type GoogleProfile = {
  remoteId: string;
  userId?: number;
} & ITimestamp;

export type LDAPStrategy = {
  id: number;
  organizationId: number;
  enabled: boolean;
  server: string;
  port: number;
  ssl: boolean;
  username: string;
  password?: string;
  passwordIsSet: boolean;
  searchBase: string;
  searchFilter: string;
  useGroups: boolean;
  groupBase?: string;
  groupFilter?: string;
  groupNameAttr?: string;
  groupDNAttr?: string;
  userEmailAttr: string;
  userNameAttr?: string;
  uidAttr?: string;
  allowEmptyEmail: boolean;
} & ITimestamp;

/**
 * @deprecated - This is issuesV1 code and will soon be removed. Do not update this code.
 */
export type Issue = APIIssue;

export type ListMatches = ListMatchesResponse;

export type UnknownDependency = {
  organizationId: number;
  root: string;
  locator: string;
  originPaths: string[];
  pathsTo: {
    paths: string[][];
  },
  depth: number;
} & ITimestamp;

export type RevisionScan = {
  id: number;
  scanned_at: string; // DateTime stamp
  organizationId: number;
  revisionId: number;
  licensingPolicyVersionId?: number;
  securityPolicyVersionId?: number;
  qualityPolicyVersionId?: number;
} & ITimestamp;

export type Revision = {
  // basic
  url?: string;
  resolved?: boolean;
  unsupported?: boolean;
  isSteady?: boolean;
  license_count?: number;
  dependency_count?: number;
  todo_count?: number;
  unresolved_issue_count?: number;
  unresolved_licensing_issue_count?: number;
  unresolved_security_issue_count?: number;
  unresolved_quality_issue_count?: number;
  // association;
  parent_locator?: string;
  parent?: Revision;
  projectId?: string;
  project?: Project;
  meta?: any;
  organizations?: any[];
  licenses?: RevisionLicense[];
  issues?: Issue[];
  issueTargets?: Issue[];

  revisionScans?: RevisionScan[];
  latestRevisionScanId?: number; // FK to last revision scan
  selectedRevisionScan?: RevisionScan; // set in data store for Project Summary

  // Note, these are raw and not filtered by HG for perf reasons
  dependencies?: Revision[];

  dependencyLocks?: Revision[];
  dependencyRoots?: Revision[];
  DependencyLock?: any;

  // pulled from API for perf reasons
  // builds?: Build[];

  LicenseGroups?: LicenseGroup[];
  // other
  source_type?: string;
  error?: string;
  attribution_file?: string;
  author?: string;
  message?: string;
  revision_timestamp?: string;
  dependency_cache_valid?: string;
  dependency_cache_updated?: string;
  package_cache_error?: string;
  unresolved_issue_counts: IssueTypesSummary;
  link?: string;
  is_stale?: boolean;
  unstale_tries?: number;
  latestHubbleAnalysisId: string;
  cliLicenseScanType: RevisionCliLicenseScanType | null;
  source?: RevisionSource;

  // container layers
  layers?: Layer[];
} & ITimestamp & Required<ILocator>;

export type RevisionDependency = {
  ignored?: boolean;

  // added by dependency api
  origin_paths?: string[];
  manual?: boolean;
  unresolved_locators?: string[];
  is_submodule?: boolean;
  type?: string;
  builds?: Build[];

  // legacy
  unreachable?: boolean;
  unsupported_fetcher?: boolean;
  excluded?: boolean;

  issueTargets?: Issue[];
} & Revision;

export type OrganizationSamlConfig = Omit<SamlStrategy, 'id'> & {
  // When hasNewConfig is true, the user has just provided a new SAML config for SAML. This is used to determine if we
  // need to prompt the user to claim a domain before saving changes.
  hasNewConfig?: boolean;
};

export type Organization = {
  id: number;
  title?: string;
  image?: string;
  demo?: boolean;
  access_level?: OrganizationAccessLevel;
  stripe_plan: FossaPlanName;
  created?: string;
  fetcher_config?: FetcherConfigMap;
  dependency_signatures?: string;
  first_party_copyright_signatures?: string[];
  stripe_id?: FossaPlanName;
  email?: string;
  size?: string;
  defaultPolicyId?: number;
  defaultSecurityPolicyId?: number;
  defaultQualityPolicyId?: number;
  defaultLicensingPolicy?: Policy;
  defaultSecurityPolicy?: Policy;
  defaultQualityPolicy?: Policy;
  defaultRoleId?: number;
  custom_billing: boolean;
  contributor_count?: number;
  contributors_updated?: string;
  custom_contributor_count?: number;
  data_update_interval?: number;
  revisionsUpdatedAt?: string;
  /**
   * @deprecated Use `useIsSecurityEnabled` helper function instead.
   * @see ./app/helpers/features.ts
   */
  security_enabled: boolean;
  quality_policy_enabled: boolean;
  subdomain?: string;
  loginSubdomain?: string;
  notification_default_email_scan_users: number[];
  notification_default_slack_scan: boolean;
  notification_default_enabled: boolean;

  users?: User[];
  accessRequests?: OrganizationAccessRequest[];
  jiras?: Array<Partial<Jira>>;
  saml?: OrganizationSamlConfig;
  slack?: unknown;
  disable_invite?: boolean;
  ldap?: LDAPStrategy;
  teams?: Team[];
  projects?: Array<{ locator: string, title: string }>;
  policies?: Policy[];
  podSources?: CocoapodsSource[];

  featureFlags: FeatureFlag[];

  // This sso object indicates only which SSO methods are available (not whether the org has SSO configured).
  sso?: {
    google: boolean;
    saml: boolean;
    ldap: boolean;
  };

  projectDefaultProvidedIntegrationhookTimeout: number;
  projectDefaultAutomatedIntegrationhookTimeout: number;
  projectDefaultIntegrationhookFailState: 'ERROR' | 'SUCCESS';
  projectDefaultSecurityStatusCheckEnabled: boolean;
  projectDefaultLicensingStatusCheckEnabled: boolean;
  projectDefaultQualityStatusCheckEnabled: boolean;
  projectDefaultSecurityIssueScanningEnabled: boolean;
  projectDefaultLicensingIssueScanningEnabled: boolean;
  projectDefaultQualityIssueScanningEnabled: boolean;
  projectDefaultExcludeBaseLayerIssuesLicensing: boolean;
  projectDefaultExcludeBaseLayerIssuesSecurity: boolean;
  projectDefaultExcludeBaseLayerIssuesQuality: boolean;
  projectDefaultStatusCheckFilterLicensing: number | null;
  projectDefaultStatusCheckFilterQuality: number | null;
  projectDefaultStatusCheckFilterVulnerability: number | null;
  defaultProjectPrivacy: 'private' | 'public' | null;
  updateHookDefaultScheduledEnabled: boolean;
  updateHookDefaultScheduledInterval?: UpdateHookScheduledInterval;
  updateHookDefaultScheduledIntervalLength?: number;
  updateHookDefaultScheduledIntervalTime?: string;
  defaultReportCustomText: string | null;
  isInternal: boolean;
  stripePriceLimit: StripePriceLimit;
  licenseConcludedEnabled: boolean;
} & ITimestamp;

export type OrganizationAccessRequest = {
  id: number;
  email: string;
  fulfilled: boolean;
  organization: {
    title: string;
  };
};

export type FeatureFlag = {
  flag: FlagName;
  organizationId: number;
} & ITimestamp;

export type User = {
  id: number;
  username?: string;
  email?: string;
  email_verified?: boolean;
  demo?: boolean;
  super?: boolean;
  joined?: string;
  last_visit?: string;
  full_name?: string;
  terms_agreed?: string;
  phone?: string;
  role?: string;
  organizationId?: number;
  sso_only: boolean;
  enabled: boolean;
  isServiceAccount: boolean;
  teams?: Team[];
  teamUsers?: Array<{
    roleId: number;
    team: { id: number; name?: string };
  }>;
  permissions?: Permission[];
  userRole?: {
    roleId: number;
    role: {
      id: number;
      isCustom: boolean;
      name: string;
      organizationId: number | null;
      scope: string;
    }
  }
  tokens?: Token[]
} & ITimestamp;

export type UpdateHook = {
  id?: number;
  active?: boolean;
  last_error?: string;
  type?: string;
  secret_key?: string;
  scheduled_interval?: UpdateHookScheduledInterval;
  scheduled_interval_length?: number;
  scheduled_interval_time?: string;
  last_scheduled_update?: string;
  ProjectLocator?: string;
} & ITimestamp;

export type Tag = {
  title: string;
} & ITimestamp;

export type ProjectLabel = {
  id: Readonly<number>;
  label: string;
};

export type Project = {
  // basic, usually included
  title?: string;
  url?: string;
  description?: string;
  public?: boolean;
  setup?: boolean;

  // associations
  policyId?: number;
  securityPolicyId?: number | null;
  qualityPolicyId?: number | null;
  sbomPolicyId?: number | null;
  policy?: Policy;
  revisions?: Revision[];
  updateHook?: UpdateHook;
  notifications?: Notification[];
  issues?: Issue[];
  rules?: Array<{}>;
  head?: Revision;
  latest?: Revision;
  contributorCount?: number;
  references?: Reference[];
  last_analyzed?: Revision;
  tags?: Tag[];
  projectCorrection?: any;
  teams?: Team[],

  // additional
  authors?: string[];
  scm_url?: string;
  manual_scm_url?: boolean;
  issue_tracker_url?: string;
  issue_tracker_type?: string;
  issueTrackerLabels: string[];
  issueTrackerIssueTypes: string[];
  policies_approve_multilicense?: boolean;
  tracking_branches?: string[];
  hidden_branches?: string[];
  default_branch?: string;
  transitive_excludes?: string[];
  bom_column_settings?: string[];
  bom_public_id?: string[];
  require_mediated_dependencies?: boolean;
  last_analyzed_revision?: string;
  invalidCredential: boolean;
  vcs_host: VCSHost;
  integrationhook_timeout: number;
  integrationhook_fail_state: 'ERROR' | 'SUCCESS';
  notes?: string;
  selectedBranch: SelectOption<string>;
  selectedRevision: SelectOption<string>;
  securityIssueScanningEnabled: boolean;
  licensingIssueScanningEnabled: boolean;
  qualityIssueScanningEnabled: boolean;
  sbomAnalysisEnabled: boolean;
  securityStatusCheckEnabled: boolean;
  licensingStatusCheckEnabled: boolean;
  qualityStatusCheckEnabled: boolean;
  reportCustomText?: string;
  type: ProjectType;
  issueTrackerCustomFields?: Record<string, JiraCustomField>;
  issueTrackerComponents?: JiraComponent[];
  issueTrackerProjectIds: string[];
  useGlobalTrackerSettings: boolean;
  excludeBaseLayerIssuesLicensing: boolean;
  excludeBaseLayerIssuesSecurity: boolean;
  excludeBaseLayerIssuesQuality: boolean;
  snippetLicensingIssueScanningEnabled: boolean;
  snippetSecurityIssueScanningEnabled: boolean;
  snippetAutoRejectMatchPercentageThreshold: number | null;
  labels: ProjectLabel[];
  //  The number here is the filterId
  filters: Partial<Record<Category, number>>;
} & ITimestamp & ILocator & IOrganizationMember;

export enum RevisionIssueScanState {
  Waiting, // either revision hasn't been analyzed yet, scanned yet or we're waiting for some API state
  NeedsConfiguration, // revision has been analyzed but last build has submitted a failure/warning
  Passing, // revision has been scanned, resolved and no issues were found
  IssuesFound, // revision has been scanned, resolved and issues were found
}

export type ProjectGroup = {
  id: number;
  title: string;
  policyId?: number;
  securityPolicyId?: number;
  qualityPolicyId?: number;
  organizationId: number;
  publicOnPortal: boolean;
  releases: ProjectGroupRelease[];
  issueCount?: number;
  reportCustomText?: string;
  // Added by redux selector
  latestRelease?: ProjectGroupLatestRelease;
  releaseGroupTeams?: Array<{ teamId: number }>;
} & ITimestamp;

export type ProjectGroupRelease = {
  id: number;
  title: string;
  projectGroupId: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  publishedOnPortal: string | null;
  reportPath: string | null;
  projects: ProjectGroupReleaseProject[];
  scans?: ProjectGroupReleaseScan[];
  issueCount?: number;
  dependency_count?: number;
  license_count?: number;
  unresolved_quality_issue_count?: number;
  unresolved_security_issue_count?: number;
  unresolved_licensing_issue_count?: number;
};

/**
 * ProjectGroupLatestRelease is a release with the full
 * Project type instead of the ProjectGroupReleaseProject type.
 * This is generated by the redux selector.
 */
export type ProjectGroupLatestRelease = {
  // These properties are added by the selector so that sorters
  // and other components don't need to recalculate this all the time.
  scanStatus?: RevisionIssueScanState;
} & ProjectGroupRelease;

export type ProjectGroupReleaseProject = {
  projectId: string;
  projectGroupReleaseId?: number;
  branch: string;
  revisionId?: any;
  createdAt?: string;
  updatedAt?: string;
  project: Project;
  revision?: Revision;
  // the latest revision available for this project + branch
  latestRevision?: Revision;
};

export type ProjectGroupReleaseScan = {
  id?: number;
  scanned_at?: string;
  organizationId?: number;
  projectGroupReleaseId: number;
  issues?: Issue[];
} & ITimestamp;

export type Policy = APIPolicy;

export type PolicyId = number;

export type Reference = {
  id: number;
  name: string;
  type: 'branch' | 'tag';
  revision_id: string;
  project_id: string;
  revision?: Revision;
};

export type LicenseGroup = {
  id: number;
  revisionId: string;
  ignored: boolean;
  signature: string;
  manual: boolean;
  isDeclared: boolean;

  Revision?: Revision;
  RevisionLicenses?: RevisionLicense[];
};

// A rule for applying based on the matchIdentityHash of a match.
export type MatchOverrideFilterByHash = {
  type: 'hash';
  matchIdentityHash: string;
  licenseId?: string;
  path?: string;
};

// A rule for applying to all matches with a particular license.
export type MatchOverrideFilterByLicense = {
  type: 'license';
  licenseId: string; // TODO(periodic): make these a more specific type.
};

// A rule for creating a new license.
export type MatchOverrideNewLicense = {
  type: 'new';
  licenseText: string;
  copyrightText: string;
};

export type MatchOverrideFilter =
  | MatchOverrideFilterByHash
  | MatchOverrideFilterByLicense
  | MatchOverrideNewLicense;

export type MatchOverride = {
  id: number;
  // The ID of the project this override applies to.
  componentProjectId: string;
  // The lew license ID to apply to the matches.  If null the match is removed.
  newLicenseIdOrRemove: string | null;
  matchFilter: MatchOverrideFilter;
} & ITimestamp;

export type MatchOverrideWithHashFilter = MatchOverride & { matchFilter: MatchOverrideFilterByHash };
export type MatchOverrideWithLicenseFilter = MatchOverride & { matchFilter: MatchOverrideFilterByLicense };
export type MatchOverrideWithNewLicense = MatchOverride & { matchFilter: MatchOverrideNewLicense };

export type NewMatchOverride = Pick<MatchOverride, 'componentProjectId' | 'newLicenseIdOrRemove' | 'matchFilter'>;

export type Build = {
  id: number;
  locator?: string;
  ownerId?: number;
  error?: string;
  warnings?: string[];
  data?: string;
  taskId?: number;
  task?: Task;
  provided?: boolean;
} & ITimestamp;

export type BuildWithRevision = Build & { revision: Revision };

export type Task = {
  id: number;
  task: string;
  started?: string;
  finished?: string;
  scheduledStartTime?: string;
  status?: 'CREATED' | 'ASSIGNED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  priority?: number;

  attempt_number?: number;
  jobToken?: string;
  maxRetries?: number;
  context?: JSON;
  pod?: string;
  hash?: string;
  logs?: TaskLog[];
} & ITimestamp;

export type TaskLog = {
  id: number;
  timestamp?: string;
  level?: string;
  message?: string;
  taskId?: number;
} & ITimestamp;

export type TeamUser = {
  id: number;
  teamId: number;
  userId: number;
  roleId: number;
} & ITimestamp;

export type TeamProject = {
  id: number;
  teamId: number;
  projectId: string;
} & ITimestamp;

export type Team = {
  id: number;
  organizationId: number;
  name: string;
  defaultRoleId: number;
  autoAddUsers: boolean;
  uniqueIdentifier?: string;
  teamUsers?: Array<Pick<TeamUser, 'userId' | 'roleId'>>;
  teamProjects?: Array<Pick<TeamProject, 'projectId'>>;
  teamProjectsCount?: number;
  teamReleaseGroups?: Array<Pick<TeamReleaseGroup, 'releaseGroupId'>>;
  teamReleaseGroupsCount?: number;
} & ITimestamp;

/**
 * A selection of teams can either be:
 * * An empty array of IDs (no teams selected)
 * * A non-empty array of IDs (some teams selected)
 * * The literal string 'null' (indicates a lack of association with any team)
 */
export type TeamSelection = number[] | 'null';

// Repository returned from services/repositories
// Note: `connectableProjects` and `connectedProjects` are plural because there
// could be multiple existing projects (via the CLI) that match this repo's "owner"
// and "repo" fields (for example, if a user checked out the same GitHub repo via
// HTTP and SSH and then analyzed them both via the CLI). Most often, however,
// there will only be zero or one `connectableProjects` for a given repo. That being
// said, we should still support this edge case. When connecting connectable projects,
// we try to connect ALL corresponding projects (instead of simply picking one at random).
export type Repository = {
  id?: number | string;
  title?: string;
  locator?: string;
  url?: string;
  description?: string;
  isFork?: boolean;
  isPrivate?: boolean;
  updated_at?: string;
  branch?: string;
  branches?: string[];
  project?: Project;
  updated?: string;
  connectableProjects?: Array<Project['locator']>;
  connectedProjects?: Array<Project['locator']>;
};

export type Ref = {
  sources: Array<{}>;
  target: {};
};

export type Filter = {
  name: string;
  value: string;
  icon: string;
};

export type ServiceFilter = {
  category: string;
  key: string;
  filters: Filter[];
  private_access?: boolean;
};

export type VCSInstanceData = {
  name: string;
  connected: boolean;
};

// Query parameters used for requests to /services endpoint
export type ServiceQueryParameters = {
  q: string;
  page: number;
  pageSize: number;
  filterType: string;
  filterValue: string;
  instanceName: string;
  // These parameters come from Github App callback
  setup_action?: string;
  installation_id?: string;
};

export type LicensingRule = APILicensingRule;

export type RuleLinkingCondition = 'STATIC' | 'DYNAMIC';
export type RuleDepthCondition = 'DIRECT' | 'DEEP';

export type QualityRule = CommonQualityRule;

export type Reducer<S, A extends { type: string }> = (state: S | undefined, action: A) => S;

export type RouterProps<P = Record<string, string>, Q = Record<string, string>> = {
  params: P;
  location: {
    pathname: string;
    search?: string;
    query: Q;
  };
};

export type FileUploadMetadata = {
  packageSpec: string;
  revision: string;
  fileName?: string;
};

export type SelectOption<T = string, L = string> = {
  //  Default to a string if no Label type argument is provided.
  label: L extends never ? string : L;
  value: T;
};

export type SelectOptionWithTooltip<T = string> = SelectOption<T> & {tooltip?: string};
export type SelectOptionWithAttributes<T = string> = SelectOption<T> & {tooltip?: string, isDisabled?: boolean};

export type CWEDescription = {
  id: number;
  title: string;
  description: string;
};

export type Layer = APILayer;

//  For the children-as-function pattern in React. Read more: https://cdb.reacttraining.com/use-a-render-prop-50de598f11ce
export type ChildrenAsFunction<RenderProps> = (renderProps: RenderProps) => ReactNode;

//  Represents a non-async Redux action
export type SyncReduxAction<T extends Record<string, unknown>> = Action & T;

//  Represents an async Redux action, before it's been resolved.
export type ReduxAction<T = unknown> = {
  type: string;
  payload(): Promise<Response & {
    data?: T;
  }>;
  meta?: Record<string, unknown>;
};

//  Represents response from an async Redux action, after it's been resolved.
export type ReduxActionResponse<T = unknown, M extends Record<string, unknown> = never> = {
  type: string;
  payload: {
    data?: T;
    url: string;
  };
  meta?: M;
};

export type FetchTimestamp = {
  timestamp: number;
};

export type AwaitedThunkResponse<T = unknown, M extends Record<string, unknown> = never> = {
  value: {
    data: T;
  };
  action: ReduxActionResponse<T, M>;
};

export type FossaSubscriptionPlan = CommonFossaSubscriptionPlan;
export type FossaSubscriptionPlanPreview = CommonFossaSubscriptionPlanPreview;

export type SetState<T> = Dispatch<SetStateAction<T>>;

export type ReduxActionWrapper = (actionCreator: ActionCreator<AnyAction>) => (...args: unknown[]) => void;

export type PaginationState = {
  page: number;
  count: number;
};

export type BaseFossaError = {
  uuid: string;
  code: number;
  message: string;
  name: string;
  privateMessage?: string;
  httpStatusCode?: number;
};
